/* GENERATED FILE — do not edit.
   Source: shared-ui/ui/FunnelChart.js
   Run "node shared-ui/sync.js" after editing the source. */
import React from 'react';
import './charts.css';

/**
 * FunnelChart — how many of the people who started are still here.
 *
 * ---------------------------------------------------------------------------
 * EVERY BAR IS MEASURED AGAINST STEP ONE, never against the step above it.
 * A funnel drawn stage-to-stage always looks healthy: each bar fills most of
 * its track and the eye reads five near-full bars. Measured against the first
 * step, a collapse looks like a collapse. That is the whole point of the
 * component — do not "fix" it to scale off the previous step.
 * ---------------------------------------------------------------------------
 *
 * ONE colour (--mark) for every bar. Bars are never coloured by rank, by
 * position, or by whether a number is judged good or bad.
 *
 * PROPS
 *   steps      [{ label, value, note }]  in funnel order, widest first.
 *              `value` may be a string ("403") — coerced with Number().
 *              `note` is an optional short factual line under the bar.
 *   format     (value) => string for the absolute figures  (default el-GR)
 *   pctLabel   names the base of the percentage column, printed as a key above
 *              the rows. The share is read long before the caption under the
 *              chart is, so the base is stated where the numbers are.
 *              (default '% του 1ου βήματος')
 *   loading    render the skeleton instead of the chart
 *   empty      text shown when `steps` is missing or empty
 *   caption    short factual line under the whole chart
 *   ariaLabel  overrides the generated text summary
 *   className  extra class on the <figure>
 *
 * Each row prints three things, in falling loudness:
 *   1. the absolute number and its share of step one          (--ink / --ink-2)
 *   2. the bar                                                (--mark)
 *   3. the drop from the previous step, muted                 (--ink-3)
 *
 * Rows are focusable, so the hover tooltip — which carries the untruncated
 * label — is reachable from the keyboard.
 */

const NBSP = ' ';
const MINUS = '−';

function toNum(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function defaultFormat(v) {
  return Number(v).toLocaleString('el-GR');
}

/** One decimal below 10% so "1,0%" does not round away to "1%". */
function pctText(part, whole) {
  if (!(whole > 0)) return '—';
  const p = (part / whole) * 100;
  const digits = p > 0 && p < 10 ? 1 : 0;
  return p.toLocaleString('el-GR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }) + '%';
}

export default function FunnelChart({
  steps,
  format,
  pctLabel = '% του 1ου βήματος',
  loading = false,
  empty = 'Δεν υπάρχουν δεδομένα χοάνης.',
  caption,
  ariaLabel,
  className,
}) {
  const fmt = typeof format === 'function' ? format : defaultFormat;
  const list = Array.isArray(steps) ? steps.filter(Boolean) : [];

  if (loading) {
    return (
      <figure className={className ? 'chart ' + className : 'chart'} aria-busy="true">
        <div className="chart-skeleton-rows">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="skeleton" style={{ display: 'block', height: '54px' }} />
          ))}
        </div>
      </figure>
    );
  }

  if (list.length === 0) {
    return (
      <figure className={className ? 'chart ' + className : 'chart'}>
        <div className="empty">{empty}</div>
        {caption ? <figcaption className="chart-cap">{caption}</figcaption> : null}
      </figure>
    );
  }

  const values = list.map((s) => toNum(s.value));
  const first = values[0];

  const summary =
    ariaLabel ||
    'Χοάνη ' + list.length + ' βημάτων, ως ποσοστό του πρώτου βήματος. ' +
      list
        .map((s, i) => s.label + ' ' + fmt(values[i]) + ', ' + pctText(values[i], first))
        .join('. ') + '.';

  return (
    <figure className={className ? 'chart ' + className : 'chart'}>
      {pctLabel ? <p className="fn-key">{pctLabel}</p> : null}

      <ol className="fn-chart" aria-label={summary}>
        {list.map((step, i) => {
          const value = values[i];
          const share = first > 0 ? value / first : 0;
          const widthPct = Math.max(0, Math.min(100, share * 100));
          const prev = i > 0 ? values[i - 1] : null;
          const drop = prev === null ? null : prev - value;
          const dropPct = prev !== null && prev > 0 ? (drop / prev) * 100 : null;

          const dropText =
            drop === null
              ? null
              : drop === 0
              ? 'Καμία απώλεια από το προηγούμενο βήμα'
              : MINUS + fmt(Math.abs(drop)) + NBSP + '(' + MINUS +
                (dropPct === null
                  ? '—'
                  : Math.abs(dropPct).toLocaleString('el-GR', {
                      minimumFractionDigits: Math.abs(dropPct) < 10 ? 1 : 0,
                      maximumFractionDigits: Math.abs(dropPct) < 10 ? 1 : 0,
                    }) + '%') +
                ') από το προηγούμενο βήμα';

          return (
            <li className="fn-row" key={step.label + '-' + i} tabIndex={0}>
              <div className="fn-head">
                <span className="fn-label" title={step.label}>{step.label}</span>
                <span className="fn-figs">
                  <b className="fn-value num">{fmt(value)}</b>
                  <span className="fn-pct num">{pctText(value, first)}</span>
                </span>
              </div>

              <div className="fn-track">
                <span
                  className="fn-fill"
                  style={{
                    width: widthPct + '%',
                    minWidth: value > 0 ? '3px' : 0,
                    animationDelay: i * 80 + 'ms',
                  }}
                />
              </div>

              {dropText || step.note ? (
                <div className="fn-meta">
                  {dropText ? <span className="fn-drop">{dropText}</span> : null}
                  {step.note ? <span className="fn-note">{step.note}</span> : null}
                </div>
              ) : null}

              <div className="chart-rowtip" role="presentation">
                <div className="chart-tip-head">{step.label}</div>
                <div className="chart-tip-row">
                  <span className="chart-tip-key">Σύνολο</span>
                  <span className="chart-tip-val">{fmt(value)}</span>
                </div>
                <div className="chart-tip-row">
                  <span className="chart-tip-key">Του 1ου βήματος</span>
                  <span className="chart-tip-val">{pctText(value, first)}</span>
                </div>
                {drop !== null ? (
                  <div className="chart-tip-row">
                    <span className="chart-tip-key">Από το προηγούμενο</span>
                    <span className="chart-tip-val">
                      {drop === 0 ? '0' : MINUS + fmt(Math.abs(drop))}
                    </span>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {caption ? <figcaption className="chart-cap">{caption}</figcaption> : null}
    </figure>
  );
}
